import { useStyles } from '@hooks/useStyles'
import { ButtonProps } from '@ui-kit/Button'
import {
  cloneElement,
  Dispatch,
  ReactElement,
  SetStateAction,
  useState
} from 'react'
import { v4 as uuidv4 } from 'uuid'

import styles from './ButtonGroup.module.scss'

export type ValueType = string | number | readonly string[] | undefined

export interface ButtonGroupProps<T> {
  buttons: ReactElement<ButtonProps>[]
  selected: T
  setSelected: Dispatch<SetStateAction<T>>
  onChange?: (value: T) => void
  className?: string | string[]
}

export const ButtonGroup = <T extends ValueType>({
  buttons,
  setSelected,
  selected,
  onChange,
  className
}: ButtonGroupProps<T>) => {
  const buttonGroupStyles = useStyles(
    {
      [styles.buttonGroup]: true
    },
    className
  )

  const handleSelect = (value: T) => {
    setSelected(value)

    if (onChange) {
      onChange(value)
    }
  }

  const buttonsWithProps = buttons.map(button => {
    const {
      props: { value }
    } = button

    return cloneElement(button, {
      key: uuidv4(),
      active: value === selected,
      onClick: () => handleSelect(value as T)
    })
  })

  return <div className={buttonGroupStyles}>{buttonsWithProps}</div>
}

export interface UncontrolledButtonGroupProps {
  buttons: ReactElement<ButtonProps>[]
  onChange?: (value: ValueType) => void
  initial?: ValueType
}

export const UncontrolledButtonGroup = ({
  buttons,
  onChange,
  initial
}: UncontrolledButtonGroupProps) => {
  const [selected, setSelected] = useState(initial)

  return (
    <ButtonGroup
      selected={selected}
      setSelected={setSelected}
      buttons={buttons}
      onChange={onChange || undefined}
    />
  )
}
